/**
 * Wraps a promise with a timeout
 * @param {Promise} promise - The promise to wrap
 * @param {number} timeoutMs - Timeout in milliseconds (default: 30000ms/30s)
 * @returns {Promise} - The original promise or rejects with timeout error
 */
export function withTimeout(promise, timeoutMs = 30000) {
  let timeoutId

  const timeoutPromise = new Promise((_, reject) => {
    timeoutId = setTimeout(
      () => reject(new Error('Request timed out. Please try again.')),
      timeoutMs,
    )
  })

  return Promise.race([promise, timeoutPromise]).finally(() => {
    clearTimeout(timeoutId)
  })
}

/**
 * Error message constants for consistent messaging
 * Exported for reuse across the application
 */
export const ERROR_MESSAGES = {
  NO_CONNECTION:
    'No internet connection. Please check your network and try again.',
  SERVER_ERROR: 'Server error occurred. Please try again later.',
  UNEXPECTED_ERROR: 'An unexpected error occurred. Please try again.',
}

/**
 * Helper to determine if an error is a network error
 * @param {Error} error - The error to check
 * @returns {boolean} - True if it's a network error
 */
export function isNetworkError(error) {
  const isOffline = typeof navigator !== 'undefined' ? !navigator.onLine : false

  return (
    error.message === 'Failed to fetch' ||
    error.message === 'Network request failed' ||
    error.message === 'Request timed out. Please try again.' ||
    isOffline
  )
}

/**
 * Get user-friendly error message from error object
 * @param {Error} error - The error object
 * @returns {string} - User-friendly error message
 */
export function getErrorMessage(error) {
  const isOffline = typeof navigator !== 'undefined' ? !navigator.onLine : false

  if (isOffline || isNetworkError(error)) {
    return ERROR_MESSAGES.NO_CONNECTION
  }

  if (error.response?.status >= 500) {
    return ERROR_MESSAGES.SERVER_ERROR
  }

  return error.message || ERROR_MESSAGES.UNEXPECTED_ERROR
}
